import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Server, HardDrive, Container, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  formatBytes,
  formatUptime,
  opsService,
  type OpsAlert,
  type OpsOverview,
} from '../../services/opsService';
import { useAuth } from '../../context/AuthContext';

function pctColor(p: number, warn = 80, crit = 90) {
  if (p >= crit) return 'text-rose-700 bg-rose-50';
  if (p >= warn) return 'text-amber-700 bg-amber-50';
  return 'text-emerald-700 bg-emerald-50';
}

function severityBadge(sev: string) {
  const map: Record<string, string> = {
    CRITICAL: 'bg-rose-100 text-rose-800',
    WARNING: 'bg-amber-100 text-amber-800',
    SECURITY: 'bg-violet-100 text-violet-800',
    INFO: 'bg-slate-100 text-slate-700',
  };
  return map[sev] || map.INFO;
}

export function InfrastructureOverviewPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('manage_ops') || hasPermission('ops.write');
  const [ov, setOv] = useState<OpsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOv(await opsService.overview());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت وضعیت زیرساخت');
      setOv(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60000);
    return () => clearInterval(t);
  }, [load]);

  const refresh = async () => {
    if (!canWrite) {
      await load();
      return;
    }
    setBusy(true);
    try {
      setOv(await opsService.refresh());
      toast.success('به‌روز شد');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    } finally {
      setBusy(false);
    }
  };

  const ack = async (a: OpsAlert) => {
    try {
      await opsService.ackAlert(a.key);
      toast.success('تأیید شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    }
  };

  const host = ov?.host;
  const alerts = ov?.activeAlerts ?? [];

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">زیرساخت</h1>
          <p className="mt-1 text-sm text-slate-500">
            وضعیت سرور، سرویس‌های مانیتور‌شده و هشدارها
            {ov?.collectedAt ? ` · آخرین جمع‌آوری: ${new Date(ov.collectedAt).toLocaleString('fa-IR')}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={busy || loading}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          به‌روزرسانی
        </button>
      </div>

      {loading && !ov ? (
        <div className="rounded-xl bg-white p-8 text-center text-slate-500 shadow-sm">در حال بارگذاری…</div>
      ) : !ov ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800">
          عامل زیرساخت در دسترس نیست. مطمئن شوید <code className="text-xs">eva-ops-agent</code> و{' '}
          <code className="text-xs">OPS_AGENT_URL</code> تنظیم شده‌اند.
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              icon={<Server className="h-5 w-5" />}
              label="CPU"
              value={`${host?.cpuPercent?.toFixed(1) ?? '—'}%`}
              sub={`load ${host?.load1?.toFixed(2) ?? '—'}`}
              tone={pctColor(host?.cpuPercent ?? 0, 85, 95)}
            />
            <MetricCard
              icon={<Server className="h-5 w-5" />}
              label="حافظه"
              value={`${host?.memPercent?.toFixed(1) ?? '—'}%`}
              sub={`${host?.memUsedMb?.toFixed(0) ?? '—'} / ${host?.memTotalMb?.toFixed(0) ?? '—'} MB`}
              tone={pctColor(host?.memPercent ?? 0, 85, 95)}
            />
            <MetricCard
              icon={<HardDrive className="h-5 w-5" />}
              label="دیسک"
              value={`${host?.diskPercent?.toFixed(1) ?? '—'}%`}
              sub={`${host?.diskAvailGb?.toFixed(1) ?? '—'} GB آزاد`}
              tone={pctColor(host?.diskPercent ?? 0)}
            />
            <MetricCard
              icon={<Server className="h-5 w-5" />}
              label="آپتایم"
              value={formatUptime(host?.uptimeSec ?? 0)}
              sub={host?.hostname || '—'}
              tone="bg-slate-50 text-slate-800"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <section className="rounded-xl bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-bold text-slate-900">
                  <Container className="h-5 w-5" /> سرویس‌ها
                </h2>
                <Link to="/infrastructure/services" className="text-sm font-medium text-teal-700 hover:underline">
                  جزئیات
                </Link>
              </div>
              <ul className="space-y-2">
                {(ov.containers ?? []).map((c) => (
                  <li key={c.name} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-medium text-slate-800">{c.name}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                        c.running ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}
                    >
                      {!c.available ? 'N/A' : c.running ? c.health || 'running' : c.status}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-xl bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-bold text-slate-900">
                  <HardDrive className="h-5 w-5" /> بکاپ و فضای ذخیره
                </h2>
                <div className="flex gap-3 text-sm">
                  <Link to="/infrastructure/backups" className="font-medium text-teal-700 hover:underline">
                    بکاپ
                  </Link>
                  <Link to="/infrastructure/storage" className="font-medium text-teal-700 hover:underline">
                    ذخیره
                  </Link>
                </div>
              </div>
              <dl className="space-y-2 text-sm">
                <Row label="آخرین بکاپ" value={ov.backup.lastSuccessAt ? new Date(ov.backup.lastSuccessAt).toLocaleString('fa-IR') : '—'} />
                <Row label="حجم پوشه بکاپ" value={ov.backup.dirUsageMb != null ? `${ov.backup.dirUsageMb.toFixed(0)} MB` : '—'} />
                <Row
                  label="تایمر بکاپ"
                  value={ov.backup.timerActive == null ? 'نامشخص' : ov.backup.timerActive ? 'فعال' : 'غیرفعال'}
                />
                <Row
                  label="کش بیلد داکر"
                  value={formatBytes(ov.storage?.find((s) => s.id === 'docker-build-cache')?.bytes ?? 0)}
                />
              </dl>
            </section>
          </div>

          <section className="rounded-xl bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-bold text-slate-900">
                <AlertTriangle className="h-5 w-5" /> هشدارهای فعال ({alerts.length})
              </h2>
              <Link to="/infrastructure/security" className="flex items-center gap-1 text-sm font-medium text-teal-700 hover:underline">
                <Shield className="h-4 w-4" /> امنیت
              </Link>
            </div>
            {alerts.length === 0 ? (
              <p className="text-sm text-slate-500">هشدار فعالی نیست.</p>
            ) : (
              <ul className="space-y-3">
                {alerts.map((a) => (
                  <li key={a.id} className="rounded-lg border border-slate-100 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <span className={`me-2 inline-flex rounded-full px-2 py-0.5 text-xs font-bold ${severityBadge(a.severity)}`}>
                          {a.severity}
                        </span>
                        <span className="font-semibold text-slate-900">{a.title}</span>
                        <p className="mt-1 text-sm text-slate-600">{a.message}</p>
                        {a.action ? <p className="mt-1 text-xs text-slate-500">اقدام: {a.action}</p> : null}
                      </div>
                      {canWrite && !a.acknowledged ? (
                        <button
                          type="button"
                          onClick={() => void ack(a)}
                          className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          تأیید
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: string;
}) {
  return (
    <div className={`rounded-xl p-4 shadow-sm ${tone}`}>
      <div className="mb-2 flex items-center gap-2 text-sm font-medium opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="mt-1 text-xs opacity-70">{sub}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-50 py-1.5 last:border-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">{value}</dd>
    </div>
  );
}
